import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { faRoute } from "@fortawesome/pro-light-svg-icons/faRoute";
import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Avatar from "antd/es/avatar/avatar";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityTypesSupportingRoutes, Route } from "@mapmycustomers/shared/types/entity";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import configService from "@app/config/ConfigService";
import authService from "@app/store/auth/AuthService";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getLegacyAppUrl } from "@app/util/appUrl";

import styles from "./RouteItem.module.scss";

interface Props {
  entityType: EntityTypesSupportingRoutes;
  isReadOnlyMember: boolean;
  onRemove?: (route: Route) => void;
  removeLoading?: boolean;
  route: Route;
}

const RouteItem: React.FC<Props> = ({
  entityType,
  isReadOnlyMember,
  onRemove,
  removeLoading,
  route,
}) => {
  const intl = useIntl();

  const [confirmVisible, setConfirmVisible] = useState<boolean>(false);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);

  const handleConfirmVisibleChange = useCallback(
    (visible: boolean) => {
      setConfirmVisible(visible);
      setTooltipVisible(false);
    },
    [setConfirmVisible, setTooltipVisible]
  );

  const handleConfirmRemove = useCallback(() => onRemove?.(route), [onRemove, route]);

  const baseOldAppUrl = configService.getBaseOldAppUrl();
  const token = authService.getToken();
  if (!token) {
    return null;
  }

  return (
    <div className={cn(styles.container, { [styles.hovered]: confirmVisible })} key={route.id}>
      <div className={styles.header}>
        <div className={styles.name}>
          <a
            href={getLegacyAppUrl(baseOldAppUrl, token, `/${entityType}/routing/edit/${route.id}`)}
            rel="noreferrer"
            target="_blank"
          >
            {route.name}
          </a>
        </div>
        <Avatar className={styles.icon} icon={<FontAwesomeIcon icon={faRoute} size="xs" />} />
      </div>

      <div className={styles.field}>
        <div className={styles.fieldName}>
          {intl.formatMessage({
            id: "recordPane.routes.field.stops",
            defaultMessage: "Stops",
            description: "Record pane - routes - Stops field name",
          })}
        </div>
        <div className={styles.fieldValue}>{route.items}</div>
      </div>

      <div className={styles.field}>
        <div className={styles.fieldName}>
          {intl.formatMessage({
            id: "recordPane.routes.field.startingLocation",
            defaultMessage: "Starting Location",
            description: "Record pane - routes - Starting Location field name",
          })}
        </div>
        <div className={styles.fieldValue}>{route.routeDetail.startAddress}</div>
      </div>

      <div className={styles.actions}>
        {!isReadOnlyMember ? (
          <Popconfirm
            arrowPointAtCenter
            cancelText={intl.formatMessage({
              id: "recordPane.routes.remove.confirmation.cancel",
              defaultMessage: "Not Yet",
              description: "No option in remove associated route confirmation in Edit pane",
            })}
            description={intl.formatMessage({
              id: "recordPane.routes.remove.confirmation",
              defaultMessage: "Are you sure you want to remove this relationship?",
              description: "Delete associated route confirmation text in Edit pane",
            })}
            okText={intl.formatMessage({
              id: "recordPane.routes.remove.confirmation.confirm",
              defaultMessage: "Remove",
              description: "Yes option in remove associated route confirmation in Edit pane",
            })}
            onConfirm={handleConfirmRemove}
            onOpenChange={handleConfirmVisibleChange}
            open={confirmVisible}
            placement="bottomRight"
          >
            <Tooltip
              mouseLeaveDelay={0}
              onOpenChange={setTooltipVisible}
              open={tooltipVisible}
              placement="left"
              title={intl.formatMessage({
                id: "recordPane.routes.remove.tooltip",
                defaultMessage: "Remove from route",
                description: "Remove from route tooltip text",
              })}
              trigger={confirmVisible ? [] : ["hover"]}
            >
              <Button
                className={styles.remove}
                icon={<FontAwesomeIcon className={styles.removeIcon} icon={faMinusCircle} />}
                iconOnly
                loading={removeLoading}
                type="link"
              />
            </Tooltip>
          </Popconfirm>
        ) : null}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

export default connect(mapStateToProps)(RouteItem);
